// components/Worksheet.jsx
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import generalLedgerAPI from '../api/generalLedgerAPI';
import chartOfAccountsAPI from '../api/chartOfAccountsAPI';
import PeriodSelect, { computePeriodRange } from '../shared/PeriodSelect';

const Worksheet = () => {
  const [worksheet, setWorksheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [period, setPeriod] = useState(() => ({ preset: 'YTD', ...computePeriodRange('YTD') }));
  const startDate = period.startDate;
  const endDate = period.endDate;
  const [beginningBalance, setBeginningBalance] = useState(0);

  // Fetch accounts on component mount and auto-select cash accounts
  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await chartOfAccountsAPI.getAll();
      if (response.success) {
        const allAccounts = response.data || [];
        setAccounts(allAccounts);
        
        // Auto-select cash accounts
        const cashAccounts = allAccounts.filter(account => 
          account.accountName.toLowerCase().includes('cash') ||
          account.accountNumber.startsWith('101') ||
          account.accountNumber.startsWith('103')
        );
        
        if (cashAccounts.length > 0) {
          setSelectedAccounts(cashAccounts.map(acc => acc._id));
        }

        // Find initial investment account (Capital account)
        const capitalAccount = allAccounts.find(account => 
          account.accountName.toLowerCase().includes('capital') ||
          account.accountNumber.startsWith('303')
        );

        if (capitalAccount) {
          // Set beginning balance from capital account (initial investment)
          setBeginningBalance(capitalAccount.currentBalance || 0);
        }
      }
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    }
  };

  const fetchWorksheet = async () => {
    if (!startDate || !endDate || selectedAccounts.length === 0) {
      setError('Please select date range and at least one account');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await generalLedgerAPI.getWorksheet(
        startDate, 
        endDate, 
        selectedAccounts
      );
      if (response.success) {
        setWorksheet(response.data);
      } else {
        setError(response.message || 'Failed to generate worksheet');
      }
    } catch (err) {
      setError(err.message || 'Error generating worksheet');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelection = (accountId) => {
    setSelectedAccounts(prev => {
      if (prev.includes(accountId)) {
        return prev.filter(id => id !== accountId);
      } else {
        return [...prev, accountId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedAccounts.length === accounts.length) {
      // If all are selected, deselect all
      setSelectedAccounts([]);
    } else {
      // Select all accounts
      setSelectedAccounts(accounts.map(acc => acc._id));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatCurrencyForCSV = (amount) => {
    return (amount || 0).toFixed(2);
  };

  // Check if account is a cash account
  const isCashAccount = (account) => {
    return account.accountName.toLowerCase().includes('cash') ||
           account.accountNumber.startsWith('101') ||
           account.accountNumber.startsWith('103');
  };

  // Calculate total for a row (sum of all account balances)
  const calculateRowTotal = (accountBalances) => {
    if (!worksheet) return 0;
    
    let total = 0;
    worksheet.selectedAccounts.forEach(account => {
      const balance = accountBalances[account._id];
      if (balance) {
        total += balance.netBalance || 0;
      }
    });
    
    return total;
  };

  // Calculate running total for cash account
  const getCashRunningBalance = (monthIndex) => {
    if (!worksheet) return beginningBalance;
    
    const cashAccount = worksheet.selectedAccounts.find(isCashAccount);
    if (!cashAccount) return beginningBalance;
    
    let runningBalance = beginningBalance;
    
    for (let i = 0; i <= monthIndex; i++) {
      const month = worksheet.monthlyData[i];
      if (month) {
        const balance = month.accountBalances[cashAccount._id];
        if (balance) {
          runningBalance += balance.netBalance;
        }
      }
    }
    
    return runningBalance;
  };

  const renderWorksheetTable = () => {
    if (!worksheet) return null;

    const cashAccount = worksheet.selectedAccounts.find(isCashAccount);
    const otherAccounts = worksheet.selectedAccounts.filter(acc => !isCashAccount(acc));

    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                  Period
                </th>
                
                {/* Cash Account Columns */}
                {cashAccount && (
                  <>
                    <th colSpan="3" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                      {cashAccount.accountName}
                    </th>
                  </>
                )}
                
                {/* Other Accounts Columns */}
                {otherAccounts.map(account => (
                  <th key={account._id} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                    {account.accountName}
                  </th>
                ))}
                
                {/* Total Column */}
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200 bg-blue-50">
                  TOTAL
                </th>
              </tr>
              
              <tr>
                <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  &nbsp;
                </th>
                
                {/* Cash Account Sub-headers */}
                {cashAccount && (
                  <>
                    <th className="px-6 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                      Debit
                    </th>
                    <th className="px-6 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Credit
                    </th>
                    <th className="px-6 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Balance
                    </th>
                  </>
                )}
                
                {/* Other Accounts Sub-headers */}
                {otherAccounts.map(account => (
                  <th key={account._id} className="px-6 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                    Balance
                  </th>
                ))}
                
                {/* Total Sub-header */}
                <th className="px-6 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200 bg-blue-50">
                  Balance
                </th>
              </tr>
            </thead>
            
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Beginning Balance Row */}
              <tr className="bg-blue-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {format(new Date(worksheet.period.startDate), 'MMM d, yyyy')} Beginning Balance
                </td>
                
                {/* Cash Account Beginning Balance */}
                {cashAccount && (
                  <>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center border-l border-gray-200">
                      -
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      -
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center text-blue-600">
                      {formatCurrency(beginningBalance)}
                    </td>
                  </>
                )}
                
                {/* Other Accounts Beginning Balance */}
                {otherAccounts.map(account => (
                  <td key={account._id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center border-l border-gray-200">
                    -
                  </td>
                ))}
                
                {/* Beginning Balance Total */}
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-center text-blue-600 border-l border-gray-200 bg-blue-50">
                  {formatCurrency(beginningBalance)}
                </td>
              </tr>

              {/* Monthly Data Rows */}
              {worksheet.monthlyData.map((month, index) => {
                const rowTotal = calculateRowTotal(month.accountBalances);
                const cashRunningBalance = getCashRunningBalance(index);

                return (
                  <tr key={month.period} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {month.displayName}
                    </td>
                    
                    {/* Cash Account Data */}
                    {cashAccount && (() => {
                      const balance = month.accountBalances[cashAccount._id] || {
                        totalDebit: 0,
                        totalCredit: 0,
                        netBalance: 0
                      };
                      
                      return (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center border-l border-gray-200">
                            {balance.totalDebit > 0 ? formatCurrency(balance.totalDebit) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                            {balance.totalCredit > 0 ? formatCurrency(balance.totalCredit) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                            <span className={
                              cashRunningBalance > 0 ? 'text-green-600' : 
                              cashRunningBalance < 0 ? 'text-red-600' : 
                              'text-gray-900'
                            }>
                              {formatCurrency(cashRunningBalance)}
                            </span>
                          </td>
                        </>
                      );
                    })()}
                    
                    {/* Other Accounts Data */}
                    {otherAccounts.map(account => {
                      const balance = month.accountBalances[account._id] || {
                        netBalance: 0
                      };
                      
                      return (
                        <td key={account._id} className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center border-l border-gray-200">
                          <span className={
                            balance.netBalance > 0 ? 'text-green-600' : 
                            balance.netBalance < 0 ? 'text-red-600' : 
                            'text-gray-500'
                          }>
                            {formatCurrency(balance.netBalance)}
                          </span>
                        </td>
                      );
                    })}
                    
                    {/* Row Total */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-center border-l border-gray-200 bg-blue-50">
                      <span className={
                        rowTotal > 0 ? 'text-green-600' : 
                        rowTotal < 0 ? 'text-red-600' : 
                        'text-gray-900'
                      }>
                        {formatCurrency(rowTotal)}
                      </span>
                    </td>
                  </tr>
                );
              })}
              
              {/* Totals Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  TOTALS
                </td>
                
                {/* Cash Account Totals */}
                {cashAccount && (() => {
                  const total = worksheet.totals.accounts[cashAccount._id] || {
                    totalDebit: 0,
                    totalCredit: 0,
                    netBalance: 0
                  };
                  
                  return (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-center border-l border-gray-200">
                        {formatCurrency(total.totalDebit)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-center">
                        {formatCurrency(total.totalCredit)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-center">
                        <span className={
                          total.netBalance > 0 ? 'text-green-600' : 
                          total.netBalance < 0 ? 'text-red-600' : 
                          'text-gray-900'
                        }>
                          {formatCurrency(total.netBalance)}
                        </span>
                      </td>
                    </>
                  );
                })()}
                
                {/* Other Accounts Totals */}
                {otherAccounts.map(account => {
                  const total = worksheet.totals.accounts[account._id] || {
                    netBalance: 0
                  };
                  
                  return (
                    <td key={account._id} className="px-6 py-4 whitespace-nowrap text-sm font-bold text-center border-l border-gray-200">
                      <span className={
                        total.netBalance > 0 ? 'text-green-600' : 
                        total.netBalance < 0 ? 'text-red-600' : 
                        'text-gray-900'
                      }>
                        {formatCurrency(total.netBalance)}
                      </span>
                    </td>
                  );
                })}
                
                {/* Grand Total */}
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-center border-l border-gray-200 bg-blue-50">
                  <span className="text-blue-600">
                    {formatCurrency(worksheet.totals.overall?.netBalance || 0)}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // CSV Export Function
  const exportToCSV = () => {
    if (!worksheet) return;

    const cashAccount = worksheet.selectedAccounts.find(isCashAccount);
    const otherAccounts = worksheet.selectedAccounts.filter(acc => !isCashAccount(acc));

    // Prepare headers
    const headers = ['Period'];
    
    if (cashAccount) {
      headers.push(`${cashAccount.accountName} - Debit`);
      headers.push(`${cashAccount.accountName} - Credit`);
      headers.push(`${cashAccount.accountName} - Balance`);
    }
    
    otherAccounts.forEach(account => {
      headers.push(`${account.accountName} - Balance`);
    });

    headers.push('TOTAL');

    // Prepare data rows
    const dataRows = [];

    // Beginning Balance Row
    const beginningRow = [`${format(new Date(worksheet.period.startDate), 'MMM d, yyyy')} Beginning Balance`];
    
    if (cashAccount) {
      beginningRow.push('', '', formatCurrencyForCSV(beginningBalance));
    }
    
    otherAccounts.forEach(() => {
      beginningRow.push('');
    });
    
    beginningRow.push(formatCurrencyForCSV(beginningBalance));
    dataRows.push(beginningRow);

    // Monthly Data Rows
    worksheet.monthlyData.forEach((month, index) => {
      const rowTotal = calculateRowTotal(month.accountBalances);
      const cashRunningBalance = getCashRunningBalance(index);

      const row = [month.displayName];
      
      // Cash Account Data
      if (cashAccount) {
        const balance = month.accountBalances[cashAccount._id] || {
          totalDebit: 0,
          totalCredit: 0,
          netBalance: 0
        };
        
        row.push(
          formatCurrencyForCSV(balance.totalDebit),
          formatCurrencyForCSV(balance.totalCredit),
          formatCurrencyForCSV(cashRunningBalance)
        );
      }
      
      // Other Accounts Data
      otherAccounts.forEach(account => {
        const balance = month.accountBalances[account._id] || {
          netBalance: 0
        };
        row.push(formatCurrencyForCSV(balance.netBalance));
      });
      
      // Row Total
      row.push(formatCurrencyForCSV(rowTotal));
      
      dataRows.push(row);
    });

    // Totals Row
    const totalsRow = ['TOTALS'];
    
    if (cashAccount) {
      const total = worksheet.totals.accounts[cashAccount._id] || {
        totalDebit: 0,
        totalCredit: 0,
        netBalance: 0
      };
      
      totalsRow.push(
        formatCurrencyForCSV(total.totalDebit),
        formatCurrencyForCSV(total.totalCredit),
        formatCurrencyForCSV(total.netBalance)
      );
    }
    
    otherAccounts.forEach(account => {
      const total = worksheet.totals.accounts[account._id] || {
        netBalance: 0
      };
      totalsRow.push(formatCurrencyForCSV(total.netBalance));
    });
    
    // Grand Total
    totalsRow.push(formatCurrencyForCSV(worksheet.totals.overall?.netBalance || 0));
    
    dataRows.push(totalsRow);

    // Combine headers and data
    const csvData = [headers, ...dataRows];

    // Convert to CSV string
    const csvContent = csvData.map(row => 
      row.map(field => `"${field}"`).join(',')
    ).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const fileName = `worksheet-${startDate}-to-${endDate}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const allSelected = selectedAccounts.length === accounts.length;
  const someSelected = selectedAccounts.length > 0 && selectedAccounts.length < accounts.length;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Accounting Worksheet</h2>
            <p className="text-gray-600 mt-1">
              Generate monthly summaries for selected accounts
            </p>
          </div>
        </div>
      </div>

      {/* Configuration Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Worksheet Configuration</h3>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
          <PeriodSelect
            preset={period.preset}
            startDate={period.startDate}
            endDate={period.endDate}
            onChange={setPeriod}
          />
        </div>

        {/* Account Selection */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Select Accounts ({selectedAccounts.length} selected)
            </label>
            <button
              onClick={handleSelectAll}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-60 overflow-y-auto p-2 border border-gray-200 rounded-md">
            {/* Select All Checkbox */}
            <div className="flex items-center border-b border-gray-200 pb-2 mb-2">
              <input
                type="checkbox"
                id="select-all"
                checked={allSelected}
                onChange={handleSelectAll}
                ref={input => {
                  if (input) {
                    input.indeterminate = someSelected;
                  }
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="select-all" className="ml-2 text-sm font-medium text-gray-700">
                {allSelected ? 'Deselect All Accounts' : 'Select All Accounts'}
              </label>
            </div>

            {/* Individual Account Checkboxes */}
            {accounts.map(account => (
              <div key={account._id} className="flex items-center">
                <input
                  type="checkbox"
                  id={`account-${account._id}`}
                  checked={selectedAccounts.includes(account._id)}
                  onChange={() => handleAccountSelection(account._id)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label 
                  htmlFor={`account-${account._id}`} 
                  className={`ml-2 text-sm ${
                    isCashAccount(account) ? 'font-medium text-blue-700' : 'text-gray-700'
                  }`}
                >
                  {account.accountNumber} - {account.accountName}
                  {isCashAccount(account) && ' (Cash)'}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {selectedAccounts.length} of {accounts.length} accounts selected
            {beginningBalance !== 0 && (
              <span className="ml-2 text-blue-600">
                • Initial Investment: {formatCurrency(beginningBalance)}
              </span>
            )}
          </div>
          <div className="flex gap-3">
            {worksheet && (
              <button
                onClick={exportToCSV}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export CSV
              </button>
            )}
            <button
              onClick={fetchWorksheet}
              disabled={loading || selectedAccounts.length === 0}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Generating Worksheet...' : 'Generate Worksheet'}
            </button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Worksheet Results */}
      {worksheet && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              Worksheet Results: {worksheet.period.displayPeriod}
            </h3>
            <div className="text-sm text-gray-500">
              {worksheet.summary.totalMonths} months • {worksheet.summary.totalTransactions} transactions
            </div>
          </div>
          
          {renderWorksheetTable()}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}
    </div>
  );
};

export default Worksheet;